export const learnerPaths = {
  affiliate: '/affiliate',
  activationKey: '/activation-key',
  desktopDownload: '/download',
  default: '/learning',
  flashcards: '/flashcards',
  googleOnboarding: '/register/google',
  passwordOnboarding: '/register/complete',
  learning: '/learning',
  learningAudio: '/learning/audio',
  learningAudioBookPattern: '/learning/audio/:bookId',
  learningEbookLessonsPattern: '/learning/ebooks/:bookId/lessons',
  learningEbookReaderPattern: '/learning/ebooks/:bookId/reader',
  learningEbooks: '/learning/ebooks',
  learningListeningQuiz: '/learning/listening-quiz',
  legacyCompat: '/__legacy-compat',
  legacyDashboard: '/dashboard',
  premium: '/premium',
  profile: '/profile',
  settings: '/settings',
  shortVideos: '/short-videos',
  teachers: '/teachers',
} as const

/** Public policy entry points. These routes intentionally sit outside auth guards. */
export const publicLegalPaths = {
  index: '/legal',
  terms: '/terms',
  privacy: '/privacy',
  cookies: '/cookies',
} as const

const LEARNING_ROUTE_SEARCH_KEYS_TO_PRESERVE = [
  'performanceTrace',
  'flipbookPerf',
] as const

interface BuildLearningRoutePathOptions {
  readonly preserveCurrentSearch?: boolean
  readonly searchParams?:
    | URLSearchParams
    | Readonly<Record<string, string | null | undefined>>
}

function getCurrentLearningRouteSearchParams() {
  const preservedSearchParams = new URLSearchParams()

  if (typeof window === 'undefined') {
    return preservedSearchParams
  }

  const currentSearchParams = new URLSearchParams(window.location.search)

  for (const key of LEARNING_ROUTE_SEARCH_KEYS_TO_PRESERVE) {
    const value = currentSearchParams.get(key)

    if (value !== null) {
      preservedSearchParams.set(key, value)
    }
  }

  return preservedSearchParams
}

function appendLearningRouteSearchParams(
  path: string,
  options?: BuildLearningRoutePathOptions,
) {
  const searchParams = options?.preserveCurrentSearch
    ? getCurrentLearningRouteSearchParams()
    : new URLSearchParams()

  if (options?.searchParams instanceof URLSearchParams) {
    options.searchParams.forEach((value, key) => {
      searchParams.set(key, value)
    })
  } else if (options?.searchParams) {
    for (const [key, value] of Object.entries(options.searchParams)) {
      if (value !== null && value !== undefined) {
        searchParams.set(key, value)
      }
    }
  }

  const query = searchParams.toString()

  return query ? `${path}?${query}` : path
}

export function buildLearningEbookLessonsPath(
  bookId: string | null | undefined,
  options?: BuildLearningRoutePathOptions,
) {
  const path = bookId
    ? `/learning/ebooks/${encodeURIComponent(bookId)}/lessons`
    : learnerPaths.learningEbooks

  return appendLearningRouteSearchParams(path, options)
}

export function buildLearningEbookReaderPath(
  bookId: string | null | undefined,
  options?: BuildLearningRoutePathOptions,
) {
  const path = bookId
    ? `/learning/ebooks/${encodeURIComponent(bookId)}/reader`
    : learnerPaths.learningEbooks

  return appendLearningRouteSearchParams(path, options)
}

export function buildLearningListeningQuizPath(
  options?: BuildLearningRoutePathOptions,
) {
  return appendLearningRouteSearchParams(
    learnerPaths.learningListeningQuiz,
    options,
  )
}

export function buildLearningAudioPath(bookId: string | null | undefined) {
  return bookId
    ? `/learning/audio/${encodeURIComponent(bookId)}`
    : learnerPaths.learningAudio
}
